import { UISref, useRouter } from "@uirouter/react";
import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { Loader } from "../../../../components/loader";
import { Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Widget } from "../../../../components/widget";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { BillingActions } from "../../../../store/billing/actions";
import { BillingDTO } from "../../../../store/billing/models/dto";
import { Billing } from "../../../../store/billing/reducer";
import { useFetchable } from "../../../../store/common/fetchable";
import { colours } from "../../../../styles/colours";
import { toDecimal } from "../../../../utils/utils";
import { BillingRoutes } from "../../routes";
import { Status } from "../common";
import { NoTransactions } from "./empty";

type SingleTransaction = BillingDTO.Responses.GetSingleTransaction.T;
type fieldNames = keyof SingleTransaction;
type SortableFieldNames = Exclude<fieldNames, "id" | "featureNames">;

function useHeaders(): TableHeaders<fieldNames> {
  const { t } = useTranslation("common");
  return useMemo(
    () => [
      {
        name: "purchaseTime",
        title: t("date", { context: "title" }),
      },
      {
        name: "credits",
        title: t("credit", { context: "title", count: 2 }),
      },
      {
        name: "equivalentIn",
        title: t("equivalent-in"),
      },
      {
        name: "state",
        title: t("state"),
      },
      {
        name: "id",
        title: "",
        centered: true,
        hiddenMobile: true,
      },
    ],
    [t]
  );
}

export const TransactionsAll = () => {
  const data = useFetchable(Billing.allTransactions, {
    pagination: {
      size: 20,
      page: 0,
    },
    sorting: {
      direction: "desc",
      field: "purchaseTime",
    },
  });

  return (
    <Loader data={[data]}>
      {([fulfilled]) => (
        <PresentationalAllTransactions TransactionList={fulfilled} />
      )}
    </Loader>
  );
};

const PresentationalAllTransactions: FunctionComponent<{
  TransactionList: BillingDTO.Responses.GetAllTransactions.T;
}> = ({ TransactionList }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation("common");

  const headers = useHeaders();

  const pageableState = useSelector<
    AppStore,
    AppStore["billing"]["allTransactions"]["pagination"]
  >((store) => store.billing.allTransactions.pagination);

  const onPaginationChange = (x: Pageable.T<SortableFieldNames>) =>
    dispatch(BillingActions.setAllTransactionsPagination(x));

  return (
    <Widget title={t("all-transactions")}>
      {TransactionList.pageable.totalElements < 1 ? (
        <NoTransactions />
      ) : (
        <Table
          data={TransactionList.content}
          head={headers}
          renderFields={{
            id: ({ id }) => (
              <UISref
                to={BillingRoutes.names.singleTransaction}
                params={{ id }}
              >
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
            purchaseTime: ({ purchaseTime }) => (
              <>{purchaseTime.local().format("yyyy-MM-DD HH:mm")}</>
            ),
            equivalentIn: ({ equivalentIn: { currency, amount } }) => (
              <>
                {currency} {toDecimal(amount)}
              </>
            ),
            credits: ({ credits }) => <>{toDecimal(credits)}</>,
            state: ({ state }) => <Status status={state}>{state}</Status>,
          }}
          rowOnClick={({ id }) => {
            router.stateService.go(BillingRoutes.names.singleTransaction, {
              id,
            });
          }}
          pagination={TransactionList.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
          unsortable={["id"]}
        />
      )}
    </Widget>
  );
};
